import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import BusinessCardContent from '../components/BusinessCardContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ORB_LOGO = "https://customer-assets.emergentagent.com/job_4f2b8787-9e39-421b-9343-dec62de2fe7e/artifacts/jrh9uqnh_Untitled%20design%20%287%29.png";

const PublicBusinessCardPage = () => {
  const { userId } = useParams();
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);
  const [centerOffset, setCenterOffset] = useState(0);

  // Force iOS safe-area (status bar / home indicator) to match the black page background
  useEffect(() => {
    const prevHtmlBg = document.documentElement.style.background;
    const prevBodyBg = document.body.style.background;
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = themeMeta ? themeMeta.getAttribute('content') : null;

    document.documentElement.style.background = '#000';
    document.body.style.background = '#000';
    if (themeMeta) themeMeta.setAttribute('content', '#000000');

    return () => {
      document.documentElement.style.background = prevHtmlBg;
      document.body.style.background = prevBodyBg;
      if (themeMeta && prevTheme !== null) themeMeta.setAttribute('content', prevTheme);
    };
  }, []);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const res = await fetch(`${API_URL}/api/card/${userId}`);
        if (!res.ok) throw new Error('Card not found');
        const data = await res.json();
        data.id = userId;
        setCardData(data);
      } catch (err) {
        setError('Business card not found');
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [userId]);

  // iter175bg — Document title shows the card owner's full name in
  // iOS Safari tab/share UI (was: "The Uplift"). Restore prior
  // title on unmount so nav-back to other routes is unaffected.
  useEffect(() => {
    if (!cardData) return;
    const name = (cardData.full_name || cardData.name || '').trim();
    if (!name) return;
    const prevTitle = document.title;
    document.title = name;
    return () => { document.title = prevTitle; };
  }, [cardData]);

  // iPad portrait only: vertically center the card by adding paddingTop.
  // Re-measures on resize / orientation change. NO effect on desktop or
  // mobile (offset stays 0). NO effect on popups (this only touches the
  // page wrapper's paddingTop).
  useEffect(() => {
    if (!wrapRef.current) return;
    const isTabletPortrait = () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px) and (orientation: portrait)').matches;
    const update = () => {
      if (!wrapRef.current) { setCenterOffset(0); return; }
      if (!isTabletPortrait()) { setCenterOffset(0); return; }
      const cardEl = wrapRef.current.querySelector('[data-testid="business-card-container"]');
      if (!cardEl) { setCenterOffset(0); return; }
      const ch = cardEl.offsetHeight;
      const vh = window.innerHeight;
      // Reserve the 20px top + 20px bottom safe-area padding the page
      // already applies; split the remaining empty space evenly above
      // and below the card.
      const available = vh - ch - 40;
      setCenterOffset(Math.max(0, Math.round(available / 2)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrapRef.current);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [cardData]);

  if (loading) {
    return (
      <div className="h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white/50 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen bg-black flex items-center justify-center p-6">
        <div className="text-center">
          <img src={ORB_LOGO} alt="ORB" className="h-12 mx-auto mb-6 object-contain" />
          <p className="text-gray-500 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      className="fixed inset-0 bg-black overflow-y-auto flex flex-col items-center"
      style={{
        paddingTop: `calc(env(safe-area-inset-top, 0px) + 20px + ${centerOffset}px)`,
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)'
      }}
      data-testid="public-business-card"
    >
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />
      <BusinessCardContent cardData={cardData} extraBottomPx={20} />
    </div>
  );
};

export default PublicBusinessCardPage;
